import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Typography, Fade, makeStyles } from '@material-ui/core';
import CustomButton from '../customButton';
import ActionButton from '../actionButton';
import ConditionalTooltip from '../conditionalTooltip';
import SectionLoader from '../sectionLoader';
import { ReactComponent as NotificationIcon } from '../../../assets/icons/bell.svg';
import { DEFAULT_FADE_IN, DEFAULT_FADE_OUT } from '../../../constants/fade';
import { isArrayEmpty, isObjectEmpty } from '../../../utility/helpers';

const useStyles = makeStyles(({ palette: { primary }, spacing }) => ({
  main: {
    backgroundColor: primary.bluish8,
    boxSizing: 'border-box',
    borderRadius: 8,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: spacing(14.5, 14, 14),
    width: '100%',
  },
  icon: {
    marginBottom: spacing(3),
  },
  title: {
    textAlign: 'center',
  },
  content: {
    textAlign: 'center',
  },
  contentMargin: {
    marginTop: spacing(2),
  },
  message: {
    textAlign: 'center',
    marginTop: spacing(1),
  },
  actionButtons: {
    display: 'flex',
    alignItems: 'center',
    marginTop: spacing(4),
  },
  iconButtonWrapper: {
    display: 'flex',
    justifyContent: 'center',
    marginTop: spacing(4),
    width: '100%',
  },
  disabledUserAction: {
    cursor: 'not-allowed',
  },
}));

const NotificationCard = ({
  className = undefined,
  titleClass = undefined,
  contentClass = undefined,
  title = '',
  titleVariant = 'h5',
  content = '',
  message = '',
  hasIcon = false,
  actionButtons = [],
  iconButtonProps = {},
  isLoading = false,
  loadingText = '',
  shouldFade = true,
  shouldFadeOutInstant = false,
  iconClass = undefined,
  customIcon: CustomIcon = null,
}) => {
  const classes = useStyles();

  const getIcon = () =>
    CustomIcon ? (
      <CustomIcon className={classNames(classes.icon, iconClass)} />
    ) : (
      <NotificationIcon className={classNames(classes.icon, iconClass)} />
    );

  return (
    <Fade
      in={shouldFade}
      timeout={{
        enter: DEFAULT_FADE_IN,
        exit: shouldFadeOutInstant ? 0 : DEFAULT_FADE_OUT,
      }}
      unmountOnExit
    >
      <div className={classNames(classes.main, className)}>
        {hasIcon && getIcon()}
        {isLoading ? (
          <>
            <SectionLoader isLoading={isLoading} relativePosition />
            <Typography
              className={classNames(classes.content, classes.contentMargin)}
              variant="body2"
            >
              {loadingText}
            </Typography>
          </>
        ) : (
          <>
            {title && (
              <Typography
                variant={titleVariant}
                className={classNames(classes.title, titleClass)}
              >
                {title}
              </Typography>
            )}
            {content && (
              <Typography
                variant="body2"
                className={classNames(
                  classes.content,
                  {
                    [classes.contentMargin]: title,
                  },
                  contentClass
                )}
              >
                {content}
              </Typography>
            )}
            {message && (
              <Typography variant="body2" className={classes.message}>
                {message}
              </Typography>
            )}
            {!isArrayEmpty(actionButtons) && (
              <div className={classes.actionButtons}>
                {actionButtons.map(
                  (
                    {
                      className: customButtonClass,
                      type,
                      onButtonClick,
                      label,
                      disabledTooltipText,
                      isDisabled,
                      shouldShowTooltip,
                      ...rest
                    },
                    index
                  ) => (
                    <ConditionalTooltip
                      key={`action_button_${index}`}
                      className={classes.disabledUserAction}
                      message={disabledTooltipText || ''}
                      addTooltip={shouldShowTooltip && isDisabled}
                    >
                      <CustomButton
                        className={classNames(customButtonClass)}
                        type={type}
                        disabled={isDisabled}
                        onClick={onButtonClick}
                        {...rest}
                      >
                        {label}
                      </CustomButton>
                    </ConditionalTooltip>
                  )
                )}
              </div>
            )}
            {!isObjectEmpty(iconButtonProps) && (
              <div className={classes.iconButtonWrapper}>
                <ActionButton {...iconButtonProps} />
              </div>
            )}
          </>
        )}
      </div>
    </Fade>
  );
};

NotificationCard.propTypes = {
  className: PropTypes.string,
  contentClass: PropTypes.string,
  titleClass: PropTypes.string,
  iconClass: PropTypes.string,
  isLoading: PropTypes.bool,
  loadingText: PropTypes.string,
  title: PropTypes.string,
  titleVariant: PropTypes.string,
  content: PropTypes.string,
  message: PropTypes.string,
  customIcon: PropTypes.object,
  actionButtons: PropTypes.arrayOf(PropTypes.shape({})),
  shouldFade: PropTypes.bool,
  shouldFadeOutInstant: PropTypes.bool,
  hasIcon: PropTypes.bool,
  iconButtonProps: PropTypes.object,
};

export default NotificationCard;
